<script setup lang="ts">
const { $api } = useNuxtApp()
const localePath = useLocalePath()

const showOverlay = ref(false)
const isLoading = ref(false)

const { data: announcements } = await useApi('/feature-announcements/pending')

async function dismissAnnouncement() {
  if (!announcements.value) return

  isLoading.value = true

  await $api('/feature-announcements/dismiss', {
    method: 'post',
    body: {
      announcement_key: announcements.value.announcement!.key,
    },
  }).finally(() => {
    isLoading.value = false
    showOverlay.value = false
  })
}

function handleActionClick() {
  if (announcements.value && announcements.value?.announcement) {
    dismissAnnouncement()
    if (announcements.value.announcement.action_url) {
      navigateTo(localePath(announcements.value.announcement.action_url))
    }
  }
}

function handleOverlayClick(event: Event) {
  // Close overlay when clicking outside the content
  if (event.target === event.currentTarget) {
    dismissAnnouncement()
  }
}

// Check for announcements when component mounts
onMounted(async () => {
  if (announcements.value?.announcement) {
    showOverlay.value = true
    await $api('/feature-announcements/shown', {
      method: 'post',
      body: {
        announcement_key: announcements.value?.announcement.key,
      },
    })
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showOverlay && announcements?.announcement"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        @click="handleOverlayClick"
      >
        <Transition
          enter-active-class="duration-300 ease-out"
          enter-from-class="scale-95 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="duration-200 ease-in"
          leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0"
        >
          <div
            v-if="showOverlay"
            class="relative w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl"
          >
            <!-- Header -->
            <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div class="flex items-center gap-3">
                <div
                  class="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center"
                >
                  <Icon
                    name="tabler:sparkles"
                    class="w-5 h-5 text-blue-600 dark:text-blue-400"
                  />
                </div>
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                  {{ announcements.announcement.title }}
                </h3>
              </div>
              <button
                type="button"
                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                @click="dismissAnnouncement"
              >
                <Icon
                  name="tabler:x"
                  class="w-5 h-5"
                />
              </button>
            </div>

            <!-- Content -->
            <div class="p-6">
              <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                {{ announcements.announcement.message }}
              </p>
            </div>

            <!-- Actions -->
            <div class="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <UiButton
                variant="outline"
                :text="$t('Not now')"
                :loading="isLoading"
                @click="dismissAnnouncement"
              />
              <UiButton
                v-if="announcements.announcement.action_url"
                :text="announcements.announcement.action_text || $t('Learn More')"
                :loading="isLoading"
                @click="handleActionClick"
              />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
